import { createContext, useContext, useEffect, useState, ReactNode } from "react";
import { User, Session } from "@supabase/supabase-js";
import { supabase } from "@/integrations/supabase/client";

interface ChronicleAuthContextType {
  user: User | null;
  session: Session | null;
  isLoading: boolean;
  signInWithOtp: (email: string) => Promise<{ error: Error | null }>;
  signOut: () => Promise<void>;
}

const ChronicleAuthContext = createContext<ChronicleAuthContextType | undefined>(undefined);

export const ChronicleAuthProvider = ({ children }: { children: ReactNode }) => {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    // Set up auth state listener FIRST
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (event, session) => {
        setSession(session);
        setUser(session?.user ?? null);
        setIsLoading(false);
        // FIX-R130-HIGH-2: clear chronicle-scoped localStorage on ANY
        // signOut path, not just our own signOut helper. Customer-portal
        // bundles render only ChronicleAuthProvider (not main AuthContext),
        // so a session that disappears via token expiry, multi-tab sign-out,
        // or any other path would otherwise leave chronicle_selected_context
        // populated for the next user.
        if (!session?.user) {
          try {
            localStorage.removeItem('chronicle_selected_context');
          } catch {
            // localStorage unavailable (embedded / SSR); ignore.
          }
        }
      }
    );

    // THEN check for existing session
    supabase.auth.getSession().then(({ data: { session } }) => {
      setSession(session);
      setUser(session?.user ?? null);
      setIsLoading(false);
    });

    return () => subscription.unsubscribe();
  }, []);

  const signInWithOtp = async (email: string) => {
    const redirectUrl = `${window.location.origin}/chronicle/auth/confirm`;
    const { error } = await supabase.auth.signInWithOtp({
      email: email.toLowerCase().trim(),
      options: {
        emailRedirectTo: redirectUrl,
      },
    });
    return { error };
  };

  const signOut = async () => {
    await supabase.auth.signOut();
    // FIX-R32-ITER2-1: Chronicle may render without the main AuthContext
    // provider mounted (customer-portal bundle), so its own onAuthStateChange
    // cleanup won't run. Clear the chronicle-specific localStorage key here
    // so the next sign-in doesn't resume with the previous customer's
    // shop/customer identifiers.
    try {
      localStorage.removeItem('chronicle_selected_context');
    } catch {
      // localStorage unavailable (embedded / SSR); ignore.
    }
  };

  return (
    <ChronicleAuthContext.Provider value={{ user, session, isLoading, signInWithOtp, signOut }}>
      {children}
    </ChronicleAuthContext.Provider>
  );
};

export const useChronicleAuth = () => {
  const context = useContext(ChronicleAuthContext);
  if (context === undefined) {
    throw new Error("useChronicleAuth must be used within a ChronicleAuthProvider");
  }
  return context;
};
